// Color picker with an optional gradient (a second color). Stored value:
//   "#rrggbb"            → solid
//   "#rrggbb,#rrggbb"    → linear gradient (start,end)
// Legacy preset names (teal/indigo/…) map to their original gradient pair so
// existing courses keep the gradient look.
const SOLID_PRESETS = ['#4f46e5', '#0d9488', '#f97316', '#7c3aed', '#db2777', '#d97706'];
const GRADIENT_PRESETS = [
  ['#6366f1', '#4338ca'], ['#14b8a6', '#0d9488'], ['#fb923c', '#ea580c'],
  ['#8b5cf6', '#6d28d9'], ['#ec4899', '#be185d'], ['#fbbf24', '#d97706'],
];
const NAME_TO_GRADIENT = {
  indigo: ['#6366f1', '#4338ca'], teal: ['#14b8a6', '#0d9488'], wpl: ['#fb923c', '#ea580c'],
  violet: ['#8b5cf6', '#6d28d9'], pink: ['#ec4899', '#be185d'], amber: ['#fbbf24', '#d97706'],
};

// → [startHex, endHex]. Solid values return the same color twice.
export function resolveColors(value, fallback = '#4f46e5') {
  if (!value || typeof value !== 'string') return [fallback, fallback];
  if (value.includes(',')) {
    const [a, b] = value.split(',').map((s) => s.trim());
    return [a || fallback, b || a || fallback];
  }
  if (value.startsWith('#')) return [value, value];
  if (NAME_TO_GRADIENT[value]) return NAME_TO_GRADIENT[value];
  return [fallback, fallback];
}

// First/primary color (for places that need a single solid color).
export function resolveColor(value, fallback = '#4f46e5') {
  return resolveColors(value, fallback)[0];
}

// CSS background string: solid when both colors match, else a linear gradient.
export function cssBackground(value) {
  const [a, b] = resolveColors(value);
  return a.toLowerCase() === b.toLowerCase() ? a : `linear-gradient(135deg, ${a}, ${b})`;
}

export default function ColorField({ value, onChange }) {
  const [c1, c2] = resolveColors(value);
  const isGradient = typeof value === 'string' && value.includes(',');

  const setSolid = (hex) => onChange(hex);
  const setGrad = (a, b) => onChange(`${a},${b}`);
  const toggleGradient = (on) => (on ? setGrad(c1, c2) : setSolid(c1));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {(isGradient ? GRADIENT_PRESETS : SOLID_PRESETS).map((p, i) => {
          const bg = Array.isArray(p) ? `linear-gradient(135deg, ${p[0]}, ${p[1]})` : p;
          return (
            <button key={i} type="button" aria-label="preset"
              onClick={() => (Array.isArray(p) ? setGrad(p[0], p[1]) : setSolid(p))}
              style={{ width: 28, height: 22, borderRadius: 6, background: bg, border: '1px solid var(--border)', cursor: 'pointer' }} />
          );
        })}
        <span style={{ width: 36, height: 22, borderRadius: 6, background: cssBackground(value), border: '1px solid var(--border)' }} title="Current" />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <label style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>Start
          <input type="color" value={c1}
            onChange={(e) => (isGradient ? setGrad(e.target.value, c2) : setSolid(e.target.value))}
            style={{ marginLeft: 6, width: 38, height: 26, border: '1px solid var(--border)', borderRadius: 6, padding: 0, background: 'none', cursor: 'pointer', verticalAlign: 'middle' }} />
        </label>
        {isGradient && (
          <label style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>End
            <input type="color" value={c2} onChange={(e) => setGrad(c1, e.target.value)}
              style={{ marginLeft: 6, width: 38, height: 26, border: '1px solid var(--border)', borderRadius: 6, padding: 0, background: 'none', cursor: 'pointer', verticalAlign: 'middle' }} />
          </label>
        )}
        <label style={{ fontSize: '0.78rem', color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input type="checkbox" checked={isGradient} onChange={(e) => toggleGradient(e.target.checked)} />
          Gradient
        </label>
      </div>
    </div>
  );
}
